import { useState, Fragment } from 'react';
import {
  TrashIcon,
  ArrowDownTrayIcon,
  ShieldExclamationIcon,
  LockClosedIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Dialog, Transition, Switch } from '@headlessui/react';
import { toast } from 'react-toastify';

import SystemService from '../../services/system.service';
import Spinner from '../../components/ui/spinner.component';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function CleanupView() {
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [currentOperation, setCurrentOperation] = useState(null); // 'clean' or 'export'
  const [deleteFutureMeetings, setDeleteFutureMeetings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deletionResults, setDeletionResults] = useState(null);

  function openPasswordModal(operation) {
    setCurrentOperation(operation);
    setPasswordModalOpen(true);
  }

  function closePasswordModal() {
    setPasswordModalOpen(false);
    setPassword('');
    setCurrentOperation(null);
  }

  function handlePasswordSubmit() {
    if (!password) {
      toast.error('Voer een wachtwoord in');
      return;
    }

    // Show confirmation dialog before executing
    setPasswordModalOpen(false);
    setConfirmModalOpen(true);
  }

  async function executeOperation() {
    setConfirmModalOpen(false);
    setLoading(true);

    try {
      if (currentOperation === 'clean') {
        const response = await SystemService.cleanSystem(password, deleteFutureMeetings);
        setDeletionResults(response.data.deletion_counts);
        toast.success('Systeem succesvol opgeschoond!');
      } else if (currentOperation === 'export') {
        await SystemService.exportSystem(password);
        toast.success('Export succesvol gedownload!');
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        toast.error('Ongeldig wachtwoord');
      } else {
        toast.error('Er is een fout opgetreden: ' + (error.response?.data || error.message));
      }
    } finally {
      setLoading(false);
      setPassword('');
      setCurrentOperation(null);
      setDeleteFutureMeetings(false);
    }
  }

  function cancelOperation() {
    setConfirmModalOpen(false);
    setPassword('');
    setCurrentOperation(null);
  }

  return (
    <>
      <main className="flex-1">
        <div className="py-6">
          <div className="mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-2xl font-semibold text-gray-900">Systeem opschonen</h1>
            <p className="mt-1 text-sm text-gray-500">
              Geavanceerde systeembewerkingen met extra beveiliging
            </p>
          </div>

          {/* Operations Grid */}
          <div className="mx-auto px-4 sm:px-6 md:px-8 mt-8">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

          {/* Clean System Card */}
          <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold leading-6 text-gray-900">Systeem opschonen</h3>
                <p className="mt-1 text-sm text-gray-500">Verwijder verouderde meetings inclusief alle bijbehorende data zoals stemmen, aanwezigheid, chat berichten, reviews en login tokens. Deze actie is permanent en kan niet ongedaan gemaakt worden.</p>
              </div>
              <TrashIcon className="h-6 w-6 text-gray-400 flex-shrink-0 ml-4" />
            </div>

            <div className="border-t border-gray-200 pt-4 flex-grow">
              <p className="text-sm font-medium text-gray-900 mb-2">Wat wordt behouden:</p>
              <ul className="text-sm text-gray-500 space-y-1 mb-4">
                <li>• Administrators en medewerkers</li>
                <li>• Gebouwen en contracten</li>
                <li>• Toekomstige meetings (optioneel)</li>
              </ul>

              <div className="flex items-center justify-between py-3 border-t border-gray-200 mt-4">
                <span className="text-sm text-gray-900">Toekomstige meetings verwijderen</span>
                <Switch
                  checked={deleteFutureMeetings}
                  onChange={setDeleteFutureMeetings}
                  className={classNames(
                    deleteFutureMeetings ? 'bg-indigo-600' : 'bg-gray-200',
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                  )}
                >
                  <span
                    className={classNames(
                      deleteFutureMeetings ? 'translate-x-6' : 'translate-x-1',
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform'
                    )}
                  />
                </Switch>
              </div>
            </div>

            <button
              type="button"
              onClick={() => openPasswordModal('clean')}
              disabled={loading}
              className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 mt-4"
            >
              Opschonen
            </button>
          </div>

          {/* Export System Card */}
          <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold leading-6 text-gray-900">Data export</h3>
                <p className="mt-1 text-sm text-gray-500">Download een volledige export van alle systeemdata in Excel formaat. Dit bevat alle gebruikers, meetings, stemresultaten en overige data voor archivering of analyse doeleinden.</p>
              </div>
              <ArrowDownTrayIcon className="h-6 w-6 text-gray-400 flex-shrink-0 ml-4" />
            </div>

            <div className="border-t border-gray-200 pt-4 flex-grow">
              <p className="text-sm font-medium text-gray-900 mb-2">Geëxporteerde data:</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Gebruikers (zonder PINs)</li>
                <li>• Meetings en gebouwen</li>
                <li>• Aanwezigheid en stemmen</li>
                <li>• Chat berichten en reviews</li>
                <li>• Contracten en ondertekeningen</li>
              </ul>
            </div>

            <button
              type="button"
              onClick={() => openPasswordModal('export')}
              disabled={loading}
              className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 mt-4"
            >
              Exporteren
            </button>
          </div>
            </div>
          </div>

          {/* Deletion Results */}
          {deletionResults && (
            <div className="mx-auto px-4 sm:px-6 md:px-8 mt-6">
              <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6">
                <h3 className="text-base font-semibold leading-6 text-gray-900 mb-4">Opschoningsresultaten</h3>
                <div className="border-t border-gray-200 pt-4">
                  <dl className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <div className="px-4 py-2 bg-gray-50 rounded">
                      <dt className="text-sm font-medium text-gray-500">Meetings</dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">{deletionResults.meetings}</dd>
                    </div>
                    <div className="px-4 py-2 bg-gray-50 rounded">
                      <dt className="text-sm font-medium text-gray-500">Gebruikers</dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">{deletionResults.users}</dd>
                    </div>
                    <div className="px-4 py-2 bg-gray-50 rounded">
                      <dt className="text-sm font-medium text-gray-500">Aanwezigheid</dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">{deletionResults.attends}</dd>
                    </div>
                    <div className="px-4 py-2 bg-gray-50 rounded">
                      <dt className="text-sm font-medium text-gray-500">Stemmen</dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">{deletionResults.votes}</dd>
                    </div>
                    <div className="px-4 py-2 bg-gray-50 rounded">
                      <dt className="text-sm font-medium text-gray-500">Manueel</dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">{deletionResults.manual_votes}</dd>
                    </div>
                    <div className="px-4 py-2 bg-gray-50 rounded">
                      <dt className="text-sm font-medium text-gray-500">Vragen</dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">{deletionResults.questions}</dd>
                    </div>
                    <div className="px-4 py-2 bg-gray-50 rounded">
                      <dt className="text-sm font-medium text-gray-500">Chats</dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">{deletionResults.chats}</dd>
                    </div>
                    <div className="px-4 py-2 bg-gray-50 rounded">
                      <dt className="text-sm font-medium text-gray-500">Reviews</dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">{deletionResults.reviews}</dd>
                    </div>
                    <div className="px-4 py-2 bg-gray-50 rounded">
                      <dt className="text-sm font-medium text-gray-500">Ondertekeningen</dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">{deletionResults.signages}</dd>
                    </div>
                    <div className="px-4 py-2 bg-gray-50 rounded">
                      <dt className="text-sm font-medium text-gray-500">Tokens</dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">{deletionResults.logintokens}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Password Modal */}
      <Transition.Root show={passwordModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closePasswordModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                      <LockClosedIcon className="h-6 w-6 text-gray-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Verificatie vereist
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Voer het administrator wachtwoord in om door te gaan
                        </p>
                      </div>
                      <div className="mt-4">
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                          placeholder="Wachtwoord"
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2"
                      onClick={handlePasswordSubmit}
                    >
                      Doorgaan
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                      onClick={closePasswordModal}
                    >
                      Annuleren
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Confirmation Modal */}
      <Transition.Root show={confirmModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={cancelOperation}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ShieldExclamationIcon className="h-6 w-6 text-gray-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        {currentOperation === 'clean' ? 'Opschonen bevestigen' : 'Export bevestigen'}
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          {currentOperation === 'clean'
                            ? 'Deze actie verwijdert permanent alle oude meetings en gerelateerde data. Weet u zeker dat u wilt doorgaan?'
                            : 'Een volledige export van alle systeemdata wordt gedownload als Excel bestand.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:ml-3 sm:w-auto"
                      onClick={executeOperation}
                    >
                      Bevestigen
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={cancelOperation}
                    >
                      Annuleren
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Loading Modal - Unclosable */}
      <Transition.Root show={loading} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => {}}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-6 pb-6 pt-5 text-center shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-8">
                  <div>
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100 mb-4">
                      <ExclamationTriangleIcon className="h-10 w-10 text-yellow-600" aria-hidden="true" />
                    </div>
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 mb-2">
                      {currentOperation === 'clean' ? 'Systeem wordt opgeschoond...' : 'Data wordt geëxporteerd...'}
                    </Dialog.Title>
                    <div className="mb-6">
                      <p className="text-sm font-medium text-yellow-700 bg-yellow-50 px-4 py-3 rounded-md border border-yellow-200">
                        ⚠️ Sluit dit venster niet
                      </p>
                      <p className="text-sm text-gray-500 mt-3">
                        {currentOperation === 'clean'
                          ? 'De database wordt opgeschoond. Dit kan enkele seconden duren.'
                          : 'De volledige export wordt voorbereid. Dit kan enkele seconden duren.'
                        }
                      </p>
                    </div>
                    <div className="py-4">
                      <Spinner size="large" />
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
